import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { getFeatureFlags, getDefaultFlags, resetFeatureFlagsCache, type FeatureFlags } from '@/config/featureFlags';

interface FeatureFlagsContextValue {
  flags: FeatureFlags;
  loading: boolean;
  refresh: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  flags: getDefaultFlags(),
  loading: true,
  refresh: async () => {},
});

export const useFeatureFlags = () => useContext(FeatureFlagsContext);

interface FeatureFlagsProviderProps {
  children: ReactNode;
}

/**
 * Provider that fetches feature flags and makes them available throughout the app.
 * Starts with sync defaults (non-blocking), then fetches remote overrides async.
 */
export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({ children }) => {
  const [flags, setFlags] = useState<FeatureFlags>(getDefaultFlags());
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      resetFeatureFlagsCache();
      const fetchedFlags = await getFeatureFlags();
      setFlags(fetchedFlags);
    } catch (error) {
      console.warn('Failed to refresh feature flags:', error);
      // Keep current flags on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Start with defaults (non-blocking), then fetch overrides
    refresh();
  }, [refresh]);

  const value = useMemo<FeatureFlagsContextValue>(
    () => ({ flags, loading, refresh }),
    [flags, loading, refresh],
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

